import api from './api';

export type MmoNicheId =
  | 'snacks' | 'candy' | 'sauce' | 'dried' | 'farm'
  | 'kitchen' | 'health' | 'beauty' | 'skincare' | 'women'
  | 'men' | 'home' | 'baby' | 'phone' | 'cases'
  | 'pets' | 'hair' | 'shoes' | 'bags' | 'sports';

export type ScriptTone = 'natural' | 'fun' | 'story' | 'expert';
export type WinProductImageSource = 'mmo' | 'web' | 'competitor_video' | 'auto';

export interface MmoNicheDefinition {
  id: MmoNicheId;
  label: string;
  group: 'food' | 'extended';
}

export interface AffiliateScriptStep {
  at: string;
  role: 'hook' | 'pain' | 'demo' | 'proof' | 'cta' | 'step';
  text: string;
}

export interface AffiliateScriptItem {
  title: string;
  steps: AffiliateScriptStep[];
  cta: string;
}

export interface AffiliateScriptResponse {
  tone: ScriptTone;
  analysis: {
    hook: string;
    pain: string;
    demo: string;
    proof: string;
    cta: string;
  };
  hooks: string[];
  scripts: AffiliateScriptItem[];
}

export interface ChannelHealthReport {
  verdict: string;
  summary: string;
  strengths: string[];
  problems: string[];
  actions: { title: string; detail: string }[];
  ideas: string[];
  sweetSpot: {
    bestBucketLabel: string;
    bestBucketAvgViews: number;
    bestBucketAvgSaves: number;
    longVideoAvgViews: number;
    recommendedDurationSec: number;
  };
  harnessActions: {
    recommendedDurationSec: number;
    recommendedTone: ScriptTone;
    priorityNiches: MmoNicheId[];
    rescueProducts: { caption: string; reason: string }[];
  };
}

export interface ViralBlueprintResult {
  download: {
    status: 'downloaded' | 'unavailable';
    tierUsed: string;
    videoPath?: string;
    error?: string;
  };
  blueprint: {
    productId: string;
    videoUrl: string;
    durationSec: number;
    openingMechanic: string;
    visualPaceSec: number;
    appetitePayoffs: string[];
    spokenHookPattern: string;
    objectionHandled: string;
  };
}

export interface HarnessLedgerSnapshot {
  threadMd: string;
  resultsTsv: string;
  rejectedMd: string;
}

export interface HermesCronJobSummary {
  id: string;
  name: string;
  schedule: string;
  enabled: boolean;
  nextRunAt?: string;
}

export interface ClusterTaskCandidate {
  model: string;
  provider: string;
  node: string;
  tier: string;
  capability_score: number;
  estimated_latency_ms: number;
  total_score: number;
  description: string;
}

export interface ClusterTaskRouteEvaluation {
  task_type: string;
  task_label: string;
  winner: ClusterTaskCandidate | null;
  fallback: ClusterTaskCandidate | null;
  candidates: ClusterTaskCandidate[];
  reasoning: string;
}

export interface ClusterModelEvaluationReport {
  timestamp: string;
  clusterRouterUrl: string;
  antigravityBridgeUrl: string;
  ollamaUrl: string;
  clusterRouterOnline: boolean;
  antigravityTurboOnline: boolean;
  antigravityServiceVersion: string;
  localOllamaOnline: boolean;
  localModels: string[];
  antigravityModels: string[];
  warmPools: Record<string, unknown>;
  tasks: ClusterTaskRouteEvaluation[];
  liveBenchmark?: {
    ranAt: string;
    localModelTested: string;
    localLatencyMs: number;
    localSampleReply: string;
    antigravityModelTested: string;
    antigravityLatencyMs: number;
    antigravityWarmHit: boolean;
    antigravitySampleReply: string;
  };
}

export interface HermesMessagingLogItem {
  id: string;
  timestamp: string;
  platform: 'zalo' | 'telegram';
  direction: 'inbound' | 'outbound';
  chatId: string;
  senderName?: string;
  text: string;
  modelUsed?: string;
}

export interface HermesMessagingStatus {
  preferredPlatform: 'zalo' | 'telegram' | 'both';
  autoReplyEnabled: boolean;
  autoPatrolReport: boolean;
  zalo: {
    enabled: boolean;
    pluginInstalled: boolean;
    hasToken: boolean;
    maskedToken: string;
    chatId: string;
    botName: string;
    connected: boolean;
    apiBase: string;
    lastError?: string;
  };
  telegram: {
    enabled: boolean;
    hasToken: boolean;
    maskedToken: string;
    chatId: string;
    botName: string;
    connected: boolean;
    apiBase: string;
    lastError?: string;
  };
  recentMessages: HermesMessagingLogItem[];
  setupGuideVi: string[];
}

export interface HermesAssistantState {
  timestamp: string;
  installed: boolean;
  hermesVersion: string;
  model: string;
  provider: string;
  gatewayActive: boolean;
  systemdService: string;
  studioOnline: boolean;
  mmoSync: Record<string, unknown> | null;
  productTiers: {
    Win_Products: string[];
    Best_Products: string[];
    Good_Products: string[];
  };
  channelHealth: Record<string, unknown> | null;
  ledgerEntryCount: number;
  cronJobs: HermesCronJobSummary[];
  skills: string[];
  summaryVi: string;
  recommendedNextStepsVi: string[];
  clusterRouting?: ClusterModelEvaluationReport;
  messaging?: HermesMessagingStatus;
}

export interface HermesChatReply {
  replyVi: string;
  modelUsed: string;
  providerUsed?: string;
  taskTypeDetected?: string;
  latencyMs?: number;
  timestamp: string;
  suggestedActions: Array<{
    action: 'sync_mmo' | 'generate_script' | 'audit_channel' | 'patrol_now';
    labelVi: string;
    payload?: Record<string, unknown>;
  }>;
}

export interface CuratedImageEntry {
  filename: string;
  semanticRole: string;
  stage: 'packaging_raw' | 'cooking_in_progress' | 'cooked_final_dish' | 'texture_closeup' | 'money_shot';
  source: 'mmo' | 'web' | 'competitor_video' | 'studio_ref';
  qualityScore: number;
  sizeBytes: number;
  reviewNoteVi: string;
  streamUrl: string;
}

export interface RejectedImageCandidate {
  candidateName: string;
  source: 'mmo' | 'web' | 'competitor_video';
  reasonVi: string;
}

export interface WinProductImageCurationReport {
  policyVersion?: 2;
  readyForProduction?: boolean;
  missingRoles?: string[];
  uniqueImageCount?: number;
  inspectionError?: string;
  productName: string;
  folderName: string;
  isDiyCookingProduct: boolean;
  matchedCompetitorVideo?: string | null;
  totalCandidatesEvaluated: number;
  approvedCount: number;
  rejectedCount: number;
  curatedImages: CuratedImageEntry[];
  rejectedCandidates: RejectedImageCandidate[];
  curationCriteriaVi: string[];
  updatedAt: string;
}

export interface WinProductInventoryItem {
  folderName: string;
  folderPath: string;
  imageFiles: string[];
  imageCount?: number;
  sourceUsed?: string;
  niche?: MmoNicheId;
  updatedAt?: string;
  isDiyCookingProduct?: boolean;
  matchedCompetitorVideo?: string | null;
  curationReport?: WinProductImageCurationReport | null;
}

export interface WinProductImageResult {
  productName: string;
  folderName: string;
  folderPath: string;
  sourceUsed: 'mmo' | 'web' | 'competitor_video' | 'fallback' | 'placeholder';
  savedImages: string[];
  details: string;
  isDiyCookingProduct?: boolean;
  matchedCompetitorVideo?: string | null;
  curationReport?: WinProductImageCurationReport;
}

export interface CompetitorAccountProfile {
  id: string;
  handle: string;
  displayName: string;
  niche: MmoNicheId;
  niches?: MmoNicheId[];
  followerEstimate: string;
  avgViewsEstimate: string;
  hookFormula: string;
  strengths: string[];
  weaknesses: string[];
  videoFiles: string[];
}

export interface AffiliateChannelProfile {
  id: string;
  name: string;
  handle: string;
  niche: MmoNicheId;
  niches?: MmoNicheId[];
  description: string;
  keywords: string[];
  competitors: CompetitorAccountProfile[];
  updatedAt: string;
}

export interface CompetitorVideoItem {
  filename: string;
  absolutePath: string;
  sizeBytes: number;
  updatedAt: string;
  publishedDate?: string;
  durationSec: number;
  width: number;
  height: number;
  inferredProductName: string;
  niche: MmoNicheId;
  dishSlug: string;
  dishName: string;
  matchedChannelId: string;
  matchedChannelName: string;
  matchedCompetitorHandle: string;
  streamUrl: string;
}

export interface CompetitorVideoMappingEntry {
  id: string;
  videoFilename: string | null;
  videoStreamUrl: string | null;
  durationSec: number | null;
  competitorHandle: string;
  competitorDisplayName: string;
  ourChannelId: string;
  ourChannelName: string;
  productName: string;
  winProductFolder: string;
  niche: MmoNicheId;
  nicheLabel: string;
  isDiyCookingProduct: boolean;
  publishedDate: string;
  isDuplicateProduct: boolean;
  duplicateNote: string;
  curatedImageCount: number;
  curatedImages: string[];
  mappingSource: 'competitor_video+mmo+web' | 'mmo+web';
}

export interface CompetitorChannelEvaluation {
  channelId: string;
  channelName: string;
  niche: MmoNicheId;
  niches?: MmoNicheId[];
  nicheLabel: string;
  evaluatedCompetitors: Array<{
    handle: string;
    displayName: string;
    niche?: MmoNicheId;
    niches?: MmoNicheId[];
    followerEstimate: string;
    avgViewsEstimate: string;
    hookFormula: string;
    strengths: string[];
    weaknesses: string[];
    videoCountInLibrary: number;
    threatLevel: 'CAO' | 'TRUNG BÌNH' | 'THẤP';
  }>;
  positioningSummary: string;
  winningPatterns: string[];
  exploitableGaps: string[];
  counterStrategy: string[];
  recommendedWinProducts: string[];
}

export interface CompetitorVideoEvaluation {
  filename: string;
  inferredProductName: string;
  niche: MmoNicheId;
  dishSlug: string;
  dishName: string;
  durationSec: number;
  resolution: string;
  scores: {
    hookStrength: number;
    productClarity: number;
    sensoryPayoff: number;
    scriptPacing: number;
    ctaUrgency: number;
    compositeScore: number;
  };
  timestampBeats: Array<{
    range: string;
    phase: string;
    observation: string;
  }>;
  strengths: string[];
  weaknesses: string[];
  howToBeat: string[];
  foodSkillRulesMatched: string[];
  extractedWinProductImages?: WinProductImageResult;
}

export interface OwnGeneratedVideoItem {
  filename: string;
  relativePath: string;
  absolutePath: string;
  dateFolder: string;
  sizeBytes: number;
  updatedAt: string;
  durationSec: number;
  width: number;
  height: number;
  inferredProductName: string;
  niche: MmoNicheId;
  dishSlug: string;
  dishName: string;
  matchedChannelId: string;
  matchedChannelName: string;
  streamUrl: string;
}

export interface OwnGeneratedVideoReview {
  video: OwnGeneratedVideoItem;
  channel: {
    id: string;
    name: string;
    handle: string;
    niche: MmoNicheId;
  };
  assessmentStatus?: 'pending-visual-review';
  scores: {
    hookStrength: number;
    productClarity: number;
    sensoryPayoff: number;
    scriptPacing: number;
    ctaUrgency: number;
    compositeScore: number;
    passedGate: boolean;
  };
  skillComplianceChecklist: Array<{
    ruleId: string;
    label: string;
    passed: boolean;
    detail: string;
  }>;
  vsCompetitorComparison: {
    competitorFilename: string;
    competitorHandle: string;
    competitorCompositeScore: number;
    ourAdvantage: string;
    gapToClose: string;
  };
  improvements: string[];
}

export interface UgcFoodSkillsAuditReport {
  domain: string;
  reviewedAt: string;
  verifiedAt: string;
  totalProfiles: number;
  profilesWithSamples: number;
  profilesWithPayoff: number;
  totalUnits: number;
  totalAtoms: number;
  genzFreshValidUntil: string;
  competitorAtomsCount: number;
  liveMcpConnected: boolean;
  liveMcpUrl: string;
  coreLockedRules: Array<{
    id: string;
    title: string;
    status: 'LOCKED_ACTIVE';
    summary: string;
  }>;
  recentAuditFixes: string[];
  dishProfiles: Array<{
    slug: string;
    name: string;
    aliasesCount: number;
    hasSamples: boolean;
    hasPayoff: boolean;
  }>;
}

export interface AutomatedResearchPipelineReport {
  executedStep: 'step1' | 'step2' | 'step3' | 'step4' | 'all';
  startedAt: string;
  completedAt: string;
  summaryVi: string;
  winProductsCount: number;
  mappingTable: CompetitorVideoMappingEntry[];
  step1Result?: {
    channelEvaluations: CompetitorChannelEvaluation[];
    videoEvaluations: CompetitorVideoEvaluation[];
  };
  step2Result?: {
    ownReviews: OwnGeneratedVideoReview[];
  };
  step3Result?: {
    curatedProducts: WinProductImageResult[];
    inventory: WinProductInventoryItem[];
  };
  step4Result?: {
    skillsAudit: UgcFoodSkillsAuditReport;
    autoAddedAtoms: string[];
  };
}

export async function fetchHermesStatus() {
  const { data } = await api.get<HermesAssistantState>('/affiliate-harness/hermes/status');
  return data;
}

export async function triggerHermesPatrol() {
  const { data } = await api.post<HermesAssistantState>('/affiliate-harness/hermes/patrol');
  return data;
}

export async function askHermesAssistantClient(payload: {
  message: string;
  productName?: string;
  niche?: MmoNicheId;
  taskType?: string;
  preferredModel?: string;
}) {
  const { data } = await api.post<HermesChatReply>('/affiliate-harness/hermes/ask', payload);
  return data;
}

export async function evaluateHermesClusterModelsClient(runLiveBenchmark = true) {
  const { data } = await api.post<ClusterModelEvaluationReport>('/affiliate-harness/hermes/evaluate-models', {
    runLiveBenchmark,
  });
  return data;
}

export async function saveHermesMessagingConfigClient(payload: {
  preferredPlatform?: 'zalo' | 'telegram' | 'both';
  autoReplyEnabled?: boolean;
  autoPatrolReport?: boolean;
  zaloBotToken?: string;
  zaloChatId?: string;
  telegramBotToken?: string;
  telegramChatId?: string;
  connectAction?: 'zalo' | 'telegram' | 'save';
}) {
  const { data } = await api.post<HermesMessagingStatus>('/affiliate-harness/hermes/messaging/config', payload);
  return data;
}

export async function sendHermesMessagingReportClient(payload?: {
  platform?: 'zalo' | 'telegram' | 'both';
  customMessage?: string;
}) {
  const { data } = await api.post<{
    sentZalo: boolean;
    sentTelegram: boolean;
    zaloError?: string;
    telegramError?: string;
    messageText: string;
    status: HermesMessagingStatus;
  }>('/affiliate-harness/hermes/messaging/send-report', payload ?? {});
  return data;
}

export async function sendInteractiveMessagingChatClient(payload: {
  platform?: 'zalo' | 'telegram';
  message: string;
  senderName?: string;
}) {
  const { data } = await api.post<{
    replyText: string;
    modelUsed: string;
    status: HermesMessagingStatus;
  }>('/affiliate-harness/hermes/messaging/chat', payload);
  return data;
}

export async function pollHermesMessagingClient() {
  const { data } = await api.post<{
    processedCount: number;
    status: HermesMessagingStatus;
  }>('/affiliate-harness/hermes/messaging/poll');
  return data;
}

export async function syncMmoNiches(niches: MmoNicheId[], autoProvision = true, curateWinImages = true) {
  const { data } = await api.post('/affiliate-harness/mmo/sync', { niches, autoProvision, curateWinImages });
  return data as {
    scannedFiles: number;
    sourceDirs: string[];
    todayWinProductsDir?: string;
    winProductsArchiveDir?: string;
    copiedFromWinCount?: number;
    downloadedNewWinCount?: number;
    products: Array<{
      productId: string;
      name: string;
      niche: MmoNicheId;
      tier: 'win' | 'best' | 'good';
      priceText?: string;
      sold?: number;
      commissionPercent?: number;
      imageUrls?: string[];
    }>;
    provisioned: Array<{
      productId: string;
      name: string;
      tier: string;
      folderPath: string;
      syncAction?: 'copied_from_win_products' | 'downloaded_new_win_images';
      savedImages?: string[];
      isDiyCookingProduct?: boolean;
      matchedCompetitorVideo?: string | null;
      curationReport?: WinProductImageCurationReport;
    }>;
    mappingTable?: CompetitorVideoMappingEntry[];
    winInventory?: WinProductInventoryItem[];
  };
}

export async function fetchWinProductsInventory() {
  const { data } = await api.get<{
    winProductsDir: string;
    todayWinProductsDir?: string;
    winProductsArchiveDir?: string;
    count: number;
    items: WinProductInventoryItem[];
    mappingTable?: CompetitorVideoMappingEntry[];
  }>('/affiliate-harness/win-products');
  return data;
}

export async function acquireWinProductImagesClient(payload: {
  productName: string;
  source: WinProductImageSource;
  competitorVideoFilename?: string;
  imageUrls?: string[];
  searchQuery?: string;
  maxImages?: number;
  minCuratedImages?: number;
}) {
  const { data } = await api.post<{
    result: WinProductImageResult;
    inventory: WinProductInventoryItem[];
    mappingTable?: CompetitorVideoMappingEntry[];
  }>('/affiliate-harness/win-products/acquire-images', payload);
  return data;
}

export async function fetchResearchMappingTable(channelId?: string) {
  const params = channelId && channelId !== 'all' ? { channelId } : undefined;
  const { data } = await api.get<{
    count: number;
    mappingTable: CompetitorVideoMappingEntry[];
  }>('/affiliate-harness/research/mapping-table', { params });
  return data.mappingTable;
}

export async function runAutomatedResearchPipelineClient(payload?: {
  step?: 'step1' | 'step2' | 'step3' | 'step4' | 'all';
  channelId?: string;
  competitorHandles?: string[];
  niches?: MmoNicheId[];
}) {
  const { data } = await api.post<AutomatedResearchPipelineReport>('/affiliate-harness/research/run-auto', payload ?? {});
  return data;
}

export async function fetchAffiliateChannels() {
  const { data } = await api.get<{ channels: AffiliateChannelProfile[] }>('/affiliate-harness/channels');
  return data.channels;
}

export async function saveAffiliateChannel(
  payload: Partial<AffiliateChannelProfile> & { name: string; niche?: MmoNicheId; niches?: MmoNicheId[] },
) {
  const { data } = await api.post<{ channels: AffiliateChannelProfile[] }>('/affiliate-harness/channels', payload);
  return data.channels;
}

export async function deleteAffiliateChannelClient(channelId: string) {
  const { data } = await api.delete<{ channels: AffiliateChannelProfile[] }>(`/affiliate-harness/channels/${encodeURIComponent(channelId)}`);
  return data.channels;
}

export async function resetDefaultAffiliateChannelsClient() {
  const { data } = await api.post<{ channels: AffiliateChannelProfile[] }>('/affiliate-harness/channels/reset-defaults');
  return data.channels;
}

export async function fetchCompetitorVideos(channelId?: string) {
  const params = channelId && channelId !== 'all' ? { channelId } : undefined;
  const { data } = await api.get<{
    competitorVideosDir: string;
    count: number;
    videos: CompetitorVideoItem[];
  }>('/affiliate-harness/competitor-videos', { params });
  return data;
}

export async function downloadCompetitorVideoClient(payload: {
  videoUrl: string;
  filenameHint?: string;
  channelId?: string;
  competitorHandle?: string;
}) {
  const { data } = await api.post<{
    download: { status: string; tierUsed: string; filePath?: string; error?: string };
    videos: CompetitorVideoItem[];
  }>('/affiliate-harness/competitor-videos/download', payload);
  return data;
}

export async function analyzeCompetitorChannelClient(payload: {
  channelId: string;
  competitorHandle?: string;
  competitorHandles?: string[];
  niches?: MmoNicheId[];
}) {
  const { data } = await api.post<CompetitorChannelEvaluation>('/affiliate-harness/competitor-analysis/channel', payload);
  return data;
}

export async function analyzeCompetitorVideoClient(payload: {
  filename: string;
  channelId?: string;
  extractWinProductImages?: boolean;
  winProductName?: string;
}) {
  const { data } = await api.post<CompetitorVideoEvaluation>('/affiliate-harness/competitor-analysis/video', payload);
  return data;
}

export async function fetchOwnGeneratedVideos(channelId?: string) {
  const params = channelId && channelId !== 'all' ? { channelId } : undefined;
  const { data } = await api.get<{
    mergedVideoDir: string;
    count: number;
    videos: OwnGeneratedVideoItem[];
  }>('/affiliate-harness/own-videos', { params });
  return data;
}

export async function reviewOwnGeneratedVideoClient(payload: {
  relativePath?: string;
  channelId?: string;
}) {
  const { data } = await api.post<OwnGeneratedVideoReview>('/affiliate-harness/own-videos/review', payload);
  return data;
}

export async function fetchFoodSkillsAudit() {
  const { data } = await api.get<UgcFoodSkillsAuditReport>('/affiliate-harness/food-skills/audit');
  return data;
}

export async function updateFoodSkillsClient(payload: {
  title: string;
  content: string;
  dishSlug?: string;
  competitorHandle?: string;
}) {
  const { data } = await api.post<{
    ok: boolean;
    addedAtomId: string;
    updatedCompetitorsMd: boolean;
    audit: UgcFoodSkillsAuditReport;
  }>('/affiliate-harness/food-skills/update', payload);
  return data;
}

export async function runAffiliateScriptStudio(payload: {
  productName: string;
  tone: ScriptTone;
  priceText?: string;
  sold?: number;
  topQuestions?: string[];
  topObjections?: string[];
  durationSec?: number;
}) {
  const { data } = await api.post<AffiliateScriptResponse>('/affiliate-harness/script-studio', payload);
  return data;
}

export async function runChannelHealthCoach(payload: unknown) {
  const { data } = await api.post<ChannelHealthReport>('/affiliate-harness/channel-health', payload);
  return data;
}

export async function deconstructWinningVideo(payload: {
  productId?: string;
  videoUrl: string;
  durationSec?: number;
  caption?: string;
  topQuestions?: string[];
  topObjections?: string[];
}) {
  const { data } = await api.post<ViralBlueprintResult>('/affiliate-harness/deconstruct-video', payload);
  return data;
}

export async function fetchHarnessLedger() {
  const { data } = await api.get<HarnessLedgerSnapshot>('/affiliate-harness/ledger');
  return data;
}
